import React, { useCallback, useEffect, useState } from 'react';
import GenericDropdown from './GenericDropdown';
import {
  getScheduleStatus,
  enableSchedule,
  disableSchedule,
  pauseSchedule,
  resumeSchedule,
  triggerScheduledIngestion,
} from '../utils/api';

// Schedule configuration panel for an ingestion group.
// Shows current schedule status + allows enable/disable/pause/resume/trigger.
// Props:
//   suffix: string
//   groupSlug: string
//   compact: boolean (if true, render inline instead of card)
const ScheduleConfig = ({ suffix, groupSlug, compact = false, onStatusChange }) => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Form state
  const [periodType, setPeriodType] = useState('hours');
  const [periodValue, setPeriodValue] = useState(1);
  const [startTime, setStartTime] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [editing, setEditing] = useState(false);

  const periodTypeOptions = [
    { id: 'minutes', name: 'Minutes' },
    { id: 'hours', name: 'Hours' },
    { id: 'days', name: 'Days' },
    { id: 'weekly', name: 'Weekly' },
    { id: 'monthly', name: 'Monthly' },
  ];

  const timezoneOptions = [
    { id: 'UTC', name: 'UTC' },
    { id: 'America/New_York', name: 'US Eastern' },
    { id: 'America/Chicago', name: 'US Central' },
    { id: 'America/Denver', name: 'US Mountain' },
    { id: 'America/Los_Angeles', name: 'US Pacific' },
    { id: 'Europe/London', name: 'UK (London)' },
    { id: 'Europe/Berlin', name: 'Europe (Berlin)' },
    { id: 'Europe/Kiev', name: 'Europe (Kyiv)' },
    { id: 'Asia/Tokyo', name: 'Japan (Tokyo)' },
    { id: 'Asia/Shanghai', name: 'China (Shanghai)' },
    { id: 'Australia/Sydney', name: 'Australia (Sydney)' },
  ];

  const loadSchedule = useCallback(async () => {
    if (!suffix || !groupSlug) return;
    setLoading(true);
    setError('');
    try {
      const result = await getScheduleStatus(suffix, groupSlug);
      const s = result.schedule || {};
      setSchedule(s);
      // Populate form with existing values
      if (s.schedule_period_type) setPeriodType(s.schedule_period_type);
      if (s.schedule_period_value) setPeriodValue(s.schedule_period_value);
      if (s.schedule_start_time) setStartTime(s.schedule_start_time);
      if (s.schedule_timezone) setTimezone(s.schedule_timezone);
    } catch (e) {
      // 404 means no schedule yet — that's fine
      if (!e.message?.includes('not found')) {
        setError(e.message);
      }
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [suffix, groupSlug]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const clearMessages = () => { setError(''); setSuccessMsg(''); };

  const handleEnable = async () => {
    clearMessages();
    if (periodType === 'minutes' && periodValue < 5) {
      setError('Minimum period is 5 minutes');
      return;
    }
    if (!periodValue || periodValue < 1) {
      setError('Period value must be a positive number');
      return;
    }
    setSaving(true);
    try {
      const result = await enableSchedule(suffix, groupSlug, {
        period_type: periodType,
        period_value: parseInt(periodValue, 10),
        start_time: startTime || undefined,
        timezone,
      });
      setSuccessMsg(result.message || 'Schedule enabled');
      setEditing(false);
      await loadSchedule();
      onStatusChange && onStatusChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    clearMessages();
    setSaving(true);
    try {
      await disableSchedule(suffix, groupSlug);
      setSuccessMsg('Schedule disabled');
      await loadSchedule();
      onStatusChange && onStatusChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async () => {
    clearMessages();
    setSaving(true);
    try {
      await pauseSchedule(suffix, groupSlug);
      setSuccessMsg('Schedule paused');
      await loadSchedule();
      onStatusChange && onStatusChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResume = async () => {
    clearMessages();
    setSaving(true);
    try {
      await resumeSchedule(suffix, groupSlug);
      setSuccessMsg('Schedule resumed');
      await loadSchedule();
      onStatusChange && onStatusChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTrigger = async () => {
    clearMessages();
    setSaving(true);
    try {
      const result = await triggerScheduledIngestion(suffix, groupSlug);
      setSuccessMsg(result.message || 'Ingestion triggered');
      await loadSchedule();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const formatPeriod = (type, value) => {
    if (!type || !value) return '—';
    if (type === 'weekly') return `Every ${value} week${value > 1 ? 's' : ''}`;
    if (type === 'monthly') return `Every ${value} month${value > 1 ? 's' : ''}`;
    return `Every ${value} ${type}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch { return iso; }
  };

  const formatDuration = (ms) => {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const statusColors = {
    active: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', icon: 'fa-play-circle' },
    paused: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'fa-pause-circle' },
    inactive: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)', icon: 'fa-stop-circle' },
    error: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', icon: 'fa-exclamation-circle' },
  };

  const isEnabled = schedule?.schedule_enabled;
  const status = schedule?.schedule_status || 'inactive';
  const statusStyle = statusColors[status] || statusColors.inactive;

  // Auto-clear success message
  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  if (loading) {
    return (
      <div className={compact ? 'schedule-config-compact' : 'schedule-config'}>
        <i className="fas fa-spinner fa-spin"></i> Loading schedule...
      </div>
    );
  }

  return (
    <div className={compact ? 'schedule-config-compact' : 'schedule-config'}>
      {error && (
        <div className="schedule-alert schedule-alert-error">
          <i className="fas fa-exclamation-circle"></i> {error}
          <button className="schedule-alert-dismiss" onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {successMsg && (
        <div className="schedule-alert schedule-alert-success">
          <i className="fas fa-check-circle"></i> {successMsg}
        </div>
      )}

      {isEnabled && !editing ? (
        /* ── Active schedule display ── */
        <div className="schedule-status-panel" style={{ background: statusStyle.bg, border: `1px solid ${statusStyle.border}`, borderRadius: '8px', padding: '16px' }}>
          <div className="schedule-status-header">
            <span className="schedule-status-badge" style={{ color: statusStyle.color, fontWeight: 600, fontSize: '1.05em' }}>
              <i className={`fas ${statusStyle.icon}`}></i> {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
            <span className="schedule-period" style={{ color: 'var(--text-secondary)', fontSize: '0.95em' }}>
              {formatPeriod(schedule.schedule_period_type, schedule.schedule_period_value)}
            </span>
          </div>

          <div className="schedule-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginTop: '12px', fontSize: '0.9em' }}>
            <div><strong>Next run:</strong> {formatDate(schedule.next_ingestion_at)}</div>
            <div><strong>Last run:</strong> {formatDate(schedule.last_ingestion_at)}</div>
            <div><strong>Last duration:</strong> {formatDuration(schedule.last_ingestion_duration_ms)}</div>
            <div><strong>Timezone:</strong> {schedule.schedule_timezone || 'UTC'}</div>
            {schedule.schedule_start_time && (
              <div><strong>Start time:</strong> {schedule.schedule_start_time}</div>
            )}
          </div>

          {schedule.last_ingestion_error && (
            <div style={{ marginTop: '10px', padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', fontSize: '0.85em', color: '#ef4444' }}>
              <i className="fas fa-exclamation-triangle"></i> Last error: {schedule.last_ingestion_error}
            </div>
          )}

          <div className="schedule-actions" style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
            {status === 'active' && (
              <button className="btn btn-sm" onClick={handlePause} disabled={saving} style={{ background: '#f59e0b', color: '#fff', border: 'none' }}>
                <i className="fas fa-pause"></i> Pause
              </button>
            )}
            {status === 'paused' && (
              <button className="btn btn-sm btn-primary" onClick={handleResume} disabled={saving}>
                <i className="fas fa-play"></i> Resume
              </button>
            )}
            <button className="btn btn-sm" onClick={handleTrigger} disabled={saving} style={{ background: 'rgba(33, 150, 243, 0.15)', color: '#2196f3', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
              <i className="fas fa-bolt"></i> Run Now
            </button>
            <button className="btn btn-sm" onClick={() => setEditing(true)} disabled={saving} style={{ background: 'var(--surface-color)', border: '1px solid var(--border-color)' }}>
              <i className="fas fa-edit"></i> Edit
            </button>
            <button className="btn btn-sm btn-danger" onClick={handleDisable} disabled={saving}>
              <i className="fas fa-stop"></i> Disable
            </button>
          </div>
        </div>
      ) : (
        /* ── Schedule editor form ── */
        <div className="schedule-editor" style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', background: 'var(--surface-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <i className="fas fa-clock" style={{ color: '#2196f3' }}></i>
            <strong style={{ fontSize: '1.05em' }}>{isEnabled ? 'Edit Schedule' : 'Enable Periodic Ingestion'}</strong>
          </div>

          <div className="schedule-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-row" style={{ margin: 0 }}>
              <label>Period Type <span className="required">*</span></label>
              <GenericDropdown
                items={periodTypeOptions}
                value={periodType}
                onChange={(id) => setPeriodType(id || 'hours')}
                placeholder="Select period"
              />
            </div>
            <div className="form-row" style={{ margin: 0 }}>
              <label>Period Value <span className="required">*</span></label>
              <input
                type="number"
                min={periodType === 'minutes' ? 5 : 1}
                value={periodValue}
                onChange={e => setPeriodValue(parseInt(e.target.value, 10) || 1)}
                style={{ width: '100%' }}
              />
              {periodType === 'minutes' && (
                <div className="hint" style={{ color: '#f59e0b', fontSize: '0.8em' }}>
                  <i className="fas fa-info-circle"></i> Minimum 5 minutes
                </div>
              )}
            </div>
            <div className="form-row" style={{ margin: 0 }}>
              <label>Start Time <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>(optional)</span></label>
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                style={{ width: '100%' }}
              />
              <div className="hint">When to start the first run</div>
            </div>
            <div className="form-row" style={{ margin: 0 }}>
              <label>Timezone</label>
              <GenericDropdown
                items={timezoneOptions}
                value={timezone}
                onChange={(id) => setTimezone(id || 'UTC')}
                placeholder="Select timezone"
              />
            </div>
          </div>

          <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--text-muted)' }}>
            {periodType === 'minutes' && periodValue >= 5 && (
              <span>Ingestion will run every <strong>{periodValue} minutes</strong></span>
            )}
            {periodType === 'hours' && (
              <span>Ingestion will run every <strong>{periodValue} hour{periodValue > 1 ? 's' : ''}</strong></span>
            )}
            {periodType === 'days' && (
              <span>Ingestion will run every <strong>{periodValue} day{periodValue > 1 ? 's' : ''}</strong></span>
            )}
            {periodType === 'weekly' && (
              <span>Ingestion will run every <strong>{periodValue} week{periodValue > 1 ? 's' : ''}</strong></span>
            )}
            {periodType === 'monthly' && (
              <span>Ingestion will run every <strong>{periodValue} month{periodValue > 1 ? 's' : ''}</strong></span>
            )}
          </div>

          <div className="hint" style={{
            padding: '10px 12px',
            marginTop: '12px',
            background: 'rgba(255, 152, 0, 0.08)',
            borderRadius: '6px',
            border: '1px solid rgba(255, 152, 0, 0.2)',
            fontSize: '0.85em'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i>{' '}
            If ingestion takes longer than the configured period, superadmins will receive email warnings.
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
            {isEnabled && (
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={handleEnable} disabled={saving}>
              {saving ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-clock"></i> {isEnabled ? 'Update Schedule' : 'Enable Schedule'}</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleConfig;
